/* Royalty header behaviour (layout-and-site-wide.md §5 to §14, §19). Vanilla custom elements, loaded deferred. */
(() => {
  const desktop = window.matchMedia('(min-width: 799px)');
  const hoverCapable = window.matchMedia('(hover: hover)');
  const CLOSE_DELAY = 400;

  /* ---------- Desktop dropdowns, wide menus and mega menus ---------- */
  class NavDropdown extends HTMLElement {
    connectedCallback() {
      this.details = this.querySelector(':scope > details');
      this.summary = this.details.querySelector(':scope > summary');
      this.panel = this.details.querySelector(':scope > [data-nav-desktop-submenu], :scope > .mega-menu');
      this.clickOnly = this.closest('[data-dropdown-click="true"]') !== null;
      this.summary.addEventListener('click', this.onClick.bind(this));
      if (!this.clickOnly) {
        this.addEventListener('mouseenter', () => hoverCapable.matches && this.open());
        this.addEventListener('mouseleave', () => hoverCapable.matches && this.scheduleClose());
      }
      this.addEventListener('keyup', (event) => {
        if (event.code !== 'Escape' || !this.details.open) return;
        event.stopPropagation();
        this.close();
        this.summary.focus();
      });
    }

    onClick(event) {
      event.preventDefault();
      if (this.details.open && this.openedAt && Date.now() - this.openedAt > 150) {
        const href = this.summary.dataset.href;
        if (href && href !== '#') window.location.href = href;
        else this.close();
        return;
      }
      this.open();
    }

    open() {
      clearTimeout(this.closeTimer);
      if (this.details.open) return;
      document.querySelectorAll('nav-dropdown').forEach((other) => {
        if (other !== this && !other.contains(this) && !this.contains(other)) other.close();
      });
      this.details.open = true;
      this.openedAt = Date.now();
      this.details.dataset.animationState = 'open';
      this.summary.setAttribute('aria-expanded', 'true');
      this.detectEdges();
    }

    scheduleClose() {
      if (this.locked) return;
      clearTimeout(this.closeTimer);
      this.closeTimer = setTimeout(() => this.close(), CLOSE_DELAY);
    }

    close() {
      if (this.locked) return;
      clearTimeout(this.closeTimer);
      if (!this.details.open) return;
      this.details.open = false;
      this.details.dataset.animationState = 'closed';
      this.summary.setAttribute('aria-expanded', 'false');
      this.querySelectorAll('nav-dropdown').forEach((child) => child.close());
    }

    detectEdges() {
      if (!this.panel || !this.panel.classList.contains('nav-desktop__tier-2--dropdown') && !this.panel.classList.contains('nav-desktop__tier-3')) return;
      this.panel.classList.remove('alternate-drop', 'alternate-drop-left');
      const rect = this.panel.getBoundingClientRect();
      if (rect.right > document.documentElement.clientWidth) this.panel.classList.add('alternate-drop');
      else if (rect.left < 0) this.panel.classList.add('alternate-drop-left');
    }
  }
  customElements.get('nav-dropdown') || customElements.define('nav-dropdown', NavDropdown);

  // Click or focus outside an open menu closes it; focus on another tier-1 item closes all.
  document.addEventListener('click', (event) => {
    document.querySelectorAll('nav-dropdown').forEach((menu) => !menu.contains(event.target) && menu.close());
  });
  document.addEventListener('focusin', (event) => {
    document.querySelectorAll('nav-dropdown').forEach((menu) => !menu.contains(event.target) && menu.close());
  });
  // Escape with the pointer (not focus) in a menu still closes it and returns focus to its parent.
  document.addEventListener('keyup', (event) => {
    if (event.code !== 'Escape') return;
    const open = [...document.querySelectorAll('nav-dropdown')].filter((menu) => menu.details.open && !menu.locked);
    open.forEach((menu) => menu.close());
    const top = open.find((menu) => !menu.parentElement.closest('nav-dropdown'));
    if (top) top.summary.focus({ preventScroll: true });
  });

  /* ---------- Localization disclosures (header and footer) ---------- */
  class DisclosureMenu extends HTMLElement {
    connectedCallback() {
      this.toggle = this.querySelector('[data-disclosure-toggle]');
      this.wrap = this.querySelector('.disclosure__list-wrap');
      this.toggle.addEventListener('click', () => {
        if (this.isOpen && this.openedByClick) return this.close();
        this.open();
        this.openedByClick = true;
      });
      this.addEventListener('mouseenter', () => hoverCapable.matches && this.open());
      this.addEventListener('mouseleave', () => hoverCapable.matches && this.close());
      this.toggle.addEventListener('focus', () => hoverCapable.matches && this.open());
      this.addEventListener('keyup', (event) => {
        if (event.code !== 'Escape') return;
        this.close();
        this.toggle.focus();
      });
      this.addEventListener('focusout', () => setTimeout(() => !this.contains(document.activeElement) && this.close()));
    }

    get isOpen() {
      return this.toggle.getAttribute('aria-expanded') === 'true';
    }

    open() {
      document.querySelectorAll('disclosure-menu').forEach((other) => other !== this && other.close());
      this.wrap.hidden = false;
      this.toggle.setAttribute('aria-expanded', 'true');
      this.toggle.classList.add('is-clicked');
      this.classList.remove('disclosure--left');
      if (this.wrap.getBoundingClientRect().right > document.documentElement.clientWidth) this.classList.add('disclosure--left');
    }

    close() {
      this.openedByClick = false;
      this.wrap.hidden = true;
      this.toggle.setAttribute('aria-expanded', 'false');
      this.toggle.classList.remove('is-clicked');
    }
  }
  customElements.get('disclosure-menu') || customElements.define('disclosure-menu', DisclosureMenu);

  /* ---------- Back to top ---------- */
  class BackToTop extends HTMLElement {
    connectedCallback() {
      let timer;
      const update = () => {
        const scrollable = document.documentElement.scrollHeight - window.innerHeight;
        this.classList.toggle('is-visible', scrollable > 0 && window.scrollY / scrollable >= 0.3);
      };
      window.addEventListener('scroll', () => {
        clearTimeout(timer);
        timer = setTimeout(update, 100);
      }, { passive: true });
      update();
      this.querySelector('button').addEventListener('click', (event) => {
        event.currentTarget.blur();
        window.scrollTo({ top: 0, behavior: 'smooth' });
      });
      const label = this.querySelector('.back-to-top__button-label--desktop');
      if (label) this.style.setProperty('--open-width', `${label.scrollWidth + 8}px`);
    }
  }
  customElements.get('back-to-top') || customElements.define('back-to-top', BackToTop);

  /* ---------- Header ---------- */
  class RoyaltyHeader extends HTMLElement {
    connectedCallback() {
      this.group = this.closest('.header-group') || document.querySelector('.header-group');
      this.mobileHeader = this.querySelector('[data-mobile-header]');
      this.drawer = this.querySelector('[data-mobile-menu]');
      this.fixed = this.dataset.fixed === 'true';
      document.body.classList.remove('mobile_nav-fixed--true', 'mobile_nav-fixed--false');
      document.body.classList.add(`mobile_nav-fixed--${this.fixed}`);

      this.initPromo();
      this.initDrawer();
      this.initMobileSearch();
      this.initCart();
      this.injectMegaMenus();
      if (this.fixed) {
        this.initPhoneSticky();
        this.initDesktopSticky();
      }
    }

    disconnectedCallback() {
      this.stickyRow?.remove();
      window.removeEventListener('scroll', this.onScroll);
      this.resizeObserver?.disconnect();
    }

    /* Announcement bar: hidden until we know it wasn't dismissed; cookie promo-banner=dismiss for 30 days. */
    initPromo() {
      const dismissed = document.cookie.split('; ').includes('promo-banner=dismiss');
      this.querySelectorAll('[data-promo-banner]').forEach((bar) => {
        if (dismissed && !Shopify.designMode) return bar.remove();
        bar.hidden = false;
        document.body.classList.add('promo-banner--show');
        bar.querySelector('[data-promo-close]').addEventListener('click', () => {
          const expires = new Date(Date.now() + 30 * 864e5).toUTCString();
          document.cookie = `promo-banner=dismiss; expires=${expires}; path=/; SameSite=None; Secure`;
          this.querySelectorAll('[data-promo-banner]').forEach((b) => b.remove());
          document.body.classList.remove('promo-banner--show');
          this.measurePhoneHeader?.();
        });
      });
    }

    /* Phone drawer */
    initDrawer() {
      if (!this.drawer) return;
      const icon = this.drawer.querySelector('[data-mobile-menu-icon]');
      const summary = this.drawer.querySelector('summary');
      summary.setAttribute('aria-expanded', 'false');
      summary.addEventListener('click', (event) => {
        event.preventDefault();
        this.drawer.open ? this.closeDrawer() : this.openDrawer();
      });
      this.drawer.addEventListener('keyup', (event) => {
        if (event.code === 'Escape' && this.drawer.open) {
          this.closeDrawer();
          summary.focus();
        }
      });
      this.openDrawer = () => {
        document.querySelector('cart-drawer.active')?.close();
        this.drawer.style.setProperty('--drawer-top', `${this.mobileHeader.querySelector('.top-bar').getBoundingClientRect().bottom}px`);
        this.drawer.open = true;
        icon.classList.add('open');
        summary.setAttribute('aria-expanded', 'true');
        document.body.classList.add('is-active');
        // The first top-level parent that isn't a switcher opens on every drawer open.
        const first = this.drawer.querySelector('#mobile_menu > li.sublink');
        if (first) this.setRow(first, true);
      };
      this.closeDrawer = () => {
        this.drawer.open = false;
        icon.classList.remove('open');
        summary.setAttribute('aria-expanded', 'false');
        document.body.classList.remove('is-active');
      };
      this.drawer.addEventListener('click', (event) => {
        const toggle = event.target.closest('[data-mobile-dropdown-rel], [data-toggle-row]');
        if (!toggle || !this.drawer.contains(toggle)) return;
        event.preventDefault();
        const row = toggle.closest('li, .dropdown_column__menu');
        this.setRow(row, !row.classList.contains('is-open'));
      });
    }

    setRow(row, open) {
      const list = row.querySelector(':scope > ul, :scope > form > ul');
      if (!list) return;
      list.hidden = !open;
      row.classList.toggle('is-open', open);
      row.classList.toggle('active', open);
      row.querySelectorAll(':scope > [aria-expanded]').forEach((el) => el.setAttribute('aria-expanded', String(open)));
    }

    /* Phone search overlay */
    initMobileSearch() {
      const panel = this.querySelector('[data-mobile-search]');
      const openLink = this.querySelector('[data-mobile-search-open]');
      if (!panel || !openLink) return;
      const overlay = document.querySelector('[data-site-overlay]');
      const close = () => {
        panel.hidden = true;
        overlay?.classList.remove('is-visible');
        document.body.classList.remove('scroll-locked');
        openLink.focus();
      };
      openLink.addEventListener('click', (event) => {
        if (desktop.matches) return;
        event.preventDefault();
        panel.hidden = false;
        overlay?.classList.add('is-visible');
        document.body.classList.add('scroll-locked');
        panel.querySelector('input[type="search"]')?.focus();
      });
      panel.querySelector('[data-mobile-search-close]').addEventListener('click', close);
      overlay?.addEventListener('click', () => !panel.hidden && close());
      panel.addEventListener('keyup', (event) => event.code === 'Escape' && close());
    }

    /* Cart buttons open Dawn's cart drawer (cart_action = ajax); every count follows #cart-icon-bubble. */
    initCart() {
      // On document: the sticky row's cart copy lives outside this element.
      document.addEventListener('click', (event) => {
        const trigger = event.target.closest('[data-cart-trigger]');
        const drawer = document.querySelector('cart-drawer');
        if (!trigger || !drawer) return;
        event.preventDefault();
        this.closeDrawer?.();
        drawer.open(trigger);
      });
      const bubble = document.getElementById('cart-icon-bubble');
      if (!bubble) return;
      new MutationObserver(() => {
        document.querySelectorAll('.mini_cart').forEach((link) => {
          if (link !== bubble) link.innerHTML = bubble.innerHTML;
        });
      }).observe(bubble, { childList: true, subtree: true });
    }

    /* Mega menus: each mega-menu-N section replaces the tier-1 items whose title handle equals its `parent`. */
    injectMegaMenus(root = document) {
      root.querySelectorAll('.mega-menu-source[data-meganav-handle]').forEach((source) => {
        const handle = source.dataset.meganavHandle;
        const panel = source.querySelector('[data-meganav-desktop]');
        const mobile = source.querySelector('[data-meganav-mobile]');
        if (!handle || !panel) return;
        this.querySelectorAll(`[data-nav-desktop-tier-1] > li[data-nav-title="${handle}"]`).forEach((item, i) => {
          const link = item.querySelector('.nav-desktop__tier-1-link');
          const href = link.dataset.href || link.getAttribute('href') || '#';
          const title = link.querySelector('span')?.textContent || link.textContent;
          const id = `mega-${source.dataset.sectionId}-${i}`;
          item.classList.add('nav-desktop__tier-1-item--widemenu-parent');
          item.dataset.meganavDesktopTarget = '';
          item.innerHTML = `<nav-dropdown><details data-nav-desktop-details><summary class="${link.className} nav-desktop__tier-1-link--parent" data-href="${href}" data-nav-desktop-link role="button" aria-expanded="false" aria-controls="${id}"><span></span><span class="icon-down-arrow svg-wrapper" aria-hidden="true"></span></summary></details></nav-dropdown>`;
          const summary = item.querySelector('summary');
          summary.querySelector('span').textContent = title.trim();
          summary.querySelector('.icon-down-arrow').innerHTML = this.caret();
          const copy = panel.cloneNode(true);
          copy.id = id;
          copy.setAttribute('data-nav-desktop-submenu', '');
          copy.classList.add('nav-desktop__tier-2', 'nav-desktop__tier-2--full-width-menu');
          item.querySelector('details').append(copy);
        });
        if (mobile) {
          this.querySelectorAll(`#mobile_menu > li[data-nav-title="${handle}"]`).forEach((row) => {
            row.classList.add('sublink', 'mobile-mega-menu-parent');
            row.querySelector(':scope > ul')?.remove();
            if (!row.querySelector(':scope > [data-mobile-dropdown-rel]')) {
              const button = document.createElement('button');
              button.type = 'button';
              button.className = 'mobile-menu__toggle';
              button.setAttribute('data-mobile-dropdown-rel', '');
              button.setAttribute('aria-expanded', 'false');
              button.setAttribute('aria-label', row.textContent.trim());
              button.innerHTML = `<span class="icon-down-arrow svg-wrapper" aria-hidden="true">${this.caret()}</span>`;
              row.append(button);
            }
            const copy = mobile.cloneNode(true);
            copy.hidden = true;
            row.append(copy);
          });
        }
      });
    }

    caret() {
      return this.querySelector('.icon-down-arrow')?.innerHTML || '';
    }

    /* Phone sticky: once the content would pass under the header, the header group gets .sticky and is fixed. */
    initPhoneSticky() {
      const placeholderHeight = () => this.mobileHeader.offsetHeight;
      this.measurePhoneHeader = () => document.body.style.setProperty('--sticky-header-height', `${placeholderHeight()}px`);
      let ticking = false;
      const check = () => {
        ticking = false;
        if (desktop.matches) {
          this.group?.classList.remove('sticky');
          document.body.style.removeProperty('margin-top');
          return;
        }
        const stuck = window.scrollY > 0;
        if (stuck !== this.group?.classList.contains('sticky')) {
          this.measurePhoneHeader();
          this.group?.classList.toggle('sticky', stuck);
          document.body.style.marginTop = stuck ? 'var(--sticky-header-height)' : '';
        }
      };
      this.onScroll = () => {
        if (ticking) return;
        ticking = true;
        setTimeout(check, 100);
      };
      window.addEventListener('scroll', this.onScroll, { passive: true });
      desktop.addEventListener('change', check);
      check();
    }

    /* Desktop sticky: a compact copy of the menu row slides in after 700px of scroll. */
    initDesktopSticky() {
      const source = this.querySelector('[data-sticky-source]');
      if (!source) return;
      const row = source.cloneNode(true);
      row.removeAttribute('data-sticky-source');
      row.classList.add('sticky_nav--stick', 'sticky_nav--unstick', 'color-page');
      row.setAttribute('aria-hidden', 'true');
      row.inert = true;
      row.querySelectorAll('[id]').forEach((el) => el.removeAttribute('id'));
      row.querySelectorAll('[aria-controls]').forEach((el) => el.removeAttribute('aria-controls'));
      row.querySelectorAll('.search-form-container').forEach((el) => el.remove());
      this.stickyRow = row;
      document.body.append(row);
      let last = 0;
      const update = () => {
        const show = desktop.matches && window.scrollY > 700;
        row.classList.toggle('sticky_nav--unstick', !show);
        row.inert = !show;
        row.setAttribute('aria-hidden', String(!show));
        if (!show) row.querySelectorAll('nav-dropdown').forEach((menu) => menu.close());
        document.body.style.setProperty('--sticky-header-height', `${show ? row.offsetHeight : 0}px`);
      };
      window.addEventListener('scroll', () => {
        const now = Date.now();
        if (now - last < 300) return void setTimeout(update, 300);
        last = now;
        update();
      }, { passive: true });
      update();
    }
  }
  customElements.get('royalty-header') || customElements.define('royalty-header', RoyaltyHeader);

  /* Theme editor: selecting a mega menu section keeps its panel open (§24). */
  if (window.Shopify && Shopify.designMode) {
    const header = () => document.querySelector('royalty-header');
    document.addEventListener('shopify:section:load', (event) => {
      if (event.target.querySelector('.mega-menu-source')) header()?.injectMegaMenus(event.target);
    });
    document.addEventListener('shopify:section:select', (event) => {
      const source = event.target.querySelector('.mega-menu-source');
      if (!source) return;
      document.querySelectorAll(`[data-desktop-header] li[data-nav-title="${source.dataset.meganavHandle}"] nav-dropdown`).forEach((menu) => {
        menu.locked = false;
        menu.open();
        menu.locked = true;
      });
    });
    document.addEventListener('shopify:section:deselect', () => {
      document.querySelectorAll('nav-dropdown').forEach((menu) => {
        menu.locked = false;
        menu.close();
      });
    });
  }
})();
